(ns minorodata.webforms.events
  (:require [re-posh.core :as re-posh]
            [mold-http.core :refer [reg-event-http]]
            [minorodata.app.utils :as app.utils]
            [minorodata.webforms.model :refer [->webform-list ->webform-table-screen]]
            [clojure.string :as c.string]))


;; =============================================================================
;; Fetch webforms data
;; =============================================================================

(defn webforms-request [_ _]
  {:http-xhrio
   {:id  :webforms-request
    :url "/api/webforms"}})

(defn webforms-callback [_ [error results]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch webforms"]}
    (let [{:keys [webforms tables]} results
          webform-list (->webform-list {:webforms webforms
                                        :tables   tables})]
      {:dispatch-n [[:webform-list/add webform-list]
                    [:webform-list/update webform-list {:replace true}]]})))

(reg-event-http
  :webforms.api/fetch-webforms
  webforms-request
  webforms-callback)


;; =============================================================================
;; Create new webform
;; =============================================================================

(defn create-form-request
  [_ [params]]
  (let [{:keys [tablename on-success]} params]
    {:http-xhrio
     {:method    :post
      :url       "/api/build-webform"
      :params    {:tablename tablename}
      :cb-params {:on-success on-success}}}))

(defn create-form-callback
  [_ [error result {:keys [on-success]}]]
  (if error
    {:dispatch [:app.events/add-snack :error "Webform not created"]}
    (do
      ;; `:table` is the created webform's tablename, not the submitted source table
      (when-let [table-name (:table result)]
        (when on-success (on-success table-name)))
      {:dispatch [:webforms.api/fetch-webforms]})))

(reg-event-http
  :webforms.api/create-form
  create-form-request
  create-form-callback)


;; =============================================================================
;; Delete a webform
;; =============================================================================

(defn delete-form-request
  [_ [params]]
  (let [{:keys [webform-id on-success]} params]
    {:http-xhrio
     {:method    :delete
      :url       (str "/api/webforms/" webform-id)
      :cb-params {:on-success on-success}}}))

(defn delete-form-callback
  [_ [error _ {:keys [on-success]}]]
  (if error
    {:dispatch [:app.events/add-snack :error "Failed to delete a webform"]}
    (do
      (when on-success (on-success))
      {:dispatch-n [[:app.events/add-snack :success "Webform deleted"]
                    [:webform-list/delete]]
       ;; Need to wait a little for webforms to be removed first before
       ;; fetching new list of webforms for everything to update properly
       :dispatch-later [{:ms       1
                         :dispatch [:webforms.api/fetch-webforms]}]})))

(reg-event-http
  :webforms.api/delete-form
  delete-form-request
  delete-form-callback)


;; =============================================================================
;; Load webform details
;; =============================================================================

(defn add-field-name [col]
  (let [{:keys [column-name]} col]
    (assoc col :field-name (c.string/replace column-name "_" "-"))))


(defn load-form-details-request [_ [on-success]]
  (let [table-screen @(re-posh/subscribe [:webform-table-screen/pull])
        {:webform-table-screen/keys
         [items-per-page page
          table-name
          order-by order-dir
          filter-field filter-type filter-value]}
        table-screen

        filter?      (every? #(seq %) [filter-type filter-field filter-value])
        params       (app.utils/assoc-some
                       {:tablename table-name}
                       :limit items-per-page
                       :page page
                       :filter-by (when filter? filter-field)
                       :filter-op (when filter? filter-type)
                       :filter-value (when filter? filter-value)
                       :order-by order-by
                       :order-dir order-dir)]
    {:http-xhrio
     {:url       "/api/webform-data"
      :params    params
      :cb-params {:form-id table-name
                  :on-success on-success}}}))

(defn load-form-details-callback
  [_ [error form-details {:keys [form-id on-success]}]]
  (if error
    {:dispatch [:app.events/add-snack :error (str "Webform data not loaded for: " form-id)]}
    (let [{:keys [columns data pagination]} form-details
          {:keys [pages-total page items-per-page items-total]} pagination]
      (when on-success (on-success))
      {:dispatch [:webform-table-screen/update
                  (->webform-table-screen
                    {:columns        (mapv add-field-name columns)
                     :data           data
                     :page           page
                     :total-pages    pages-total
                     :items-total    items-total
                     :items-per-page items-per-page})
                  {:replace true}]})))

(reg-event-http
  :webforms.api/load-form-details
  load-form-details-request
  load-form-details-callback)


;; =============================================================================
;; Save new webform row
;; =============================================================================

(defn save-new-row-request [_ [form-id data resolve reject silent?]]
  (let [table-screen   @(re-posh/subscribe [:webform-table-screen/pull])
        {:webform-table-screen/keys [columns]} table-screen
        columns-names  (->> columns
                            (filter #(not (c.string/starts-with? (:field-name %) "-kwf")))
                            (sort-by :ordinal-position)
                            (map (comp keyword :field-name)))
        extract-values (apply juxt columns-names)
        values         (extract-values data)]
    {:http-xhrio
     {:method    :post
      :url       "/api/webform-data"
      :cb-params {:form-id form-id
                  :resolve resolve
                  :reject  reject
                  :silent? silent?}
      :params    {:tablename form-id
                  :values    values
                  :uuid      (str (random-uuid))
                  :button    "add"}}}))

(defn save-new-row-callback
  [_ [error _ {:keys [resolve reject silent?]}]]
  (if error
    (do
      (reject #js {})
      {:dispatch [:app.events/add-snack :error "Failed to create new entry"]})
    (do
      (resolve #js {})
      (when-not silent?
        {:dispatch [:webforms.api/load-form-details]}))))

(reg-event-http
  :webforms.edit/save-new-row
  save-new-row-request
  save-new-row-callback)


;; =============================================================================
;; Update webform row
;; =============================================================================

(defn save-row-request
  [_ [form-id row resolve reject silent?]]
  (let [table-screen   @(re-posh/subscribe [:webform-table-screen/pull])
        {:webform-table-screen/keys [columns]} table-screen
        columns-names  (->> columns
                            (sort-by :ordinal-position)
                            (filter #(not (c.string/starts-with? (:field-name %) "-kwf")))
                            (map (comp keyword :field-name)))
        extract-values (apply juxt columns-names)
        values         (extract-values row)
        row-uid        (:-kwf-uuid row)]
    {:http-xhrio
     {:id        row-uid
      :method    :post
      :url       "/api/webform-data"
      :cb-params {:form-id form-id
                  :row-uid row-uid
                  :resolve resolve
                  :reject  reject
                  :silent? silent?}
      :params    {:tablename form-id
                  :values    values
                  :uuid      row-uid
                  :button    "update"}}}))

(defn save-row-callback
  [_ [error _ {:keys [resolve reject silent?]}]]
  (if error
    (do
      (reject #js {})
      {:dispatch [:app.events/add-snack :error "Failed to save the entry"]})
    (do
      (resolve #js {})
      (when-not silent?
        {:dispatch [:webforms.api/load-form-details]}))))

(reg-event-http
  :webforms.edit/save-row
  save-row-request
  save-row-callback)


;; =============================================================================
;; Delete webform row
;; =============================================================================

(defn delete-row-request
  [_ [form-id row-uid resolve reject silent?]]
  {:http-xhrio
   {:id        row-uid
    :method    :post
    :url       "/api/webform-data"
    :cb-params {:form-id form-id
                :row-uid row-uid
                :resolve resolve
                :reject  reject
                :silent? silent?}
    :params    {:tablename form-id
                :uuid      row-uid
                :button    "remove"}}})

(defn delete-row-callback
  [_ [error _ {:keys [resolve reject silent?]}]]
  (if error
    (do
      (reject #js {})
      {:dispatch [:app.events/add-snack :error "Failed to delete an entry"]})
    (do
      (resolve #js {})
      (when-not silent?
        {:dispatch [:webforms.api/load-form-details]}))))

(reg-event-http
  :webforms.edit/delete-row
  delete-row-request
  delete-row-callback)


;; =============================================================================
;; Create new table and webform
;; =============================================================================

(defn create-table-request
  [_ [params]]
  (let [{:keys [data on-success on-close]} params
        {:keys [table-name columns]} data]
    {:dispatch [:webform-list/update {:webform-list/new-table-error ""}]
     :http-xhrio
               {:method    :post
                :id        :create-table-request
                :url       "/api/webforms/create-table"
                :params    {:table-name table-name
                            :columns    columns}
                :cb-params {:on-success on-success
                            :on-close   on-close
                            :table-name table-name}}}))

(defn create-table-callback
  [_ [error result {:keys [on-success on-close table-name]}]]
  (if error
    {:dispatch-n [[:app.events/add-snack :error "Table not created"]
                  [:webform-list/update
                   (->webform-list
                     {:new-table-error (get-in error [:response :message])})]]}
    (do
      (if-let [created-table (:table result)]
        (on-success created-table)
        (on-close))
      {:dispatch-n [[:webforms.api/fetch-webforms]
                    [:app.events/add-snack :success (str "Created table " table-name)]]})))

(reg-event-http
  :webforms.api/create-table
  create-table-request
  create-table-callback)



;; =============================================================================
;; Delete webform row
;; =============================================================================

(defn webform-bulk-update-request
  [_ {:keys [form-id data on-success on-failure]}]
  {:http-xhrio
   {:method    :post
    :url       "/api/webform-data/bulk-edit"
    :cb-params {:form-id form-id
                :data    data
                :on-success on-success
                :on-failure on-failure}
    :params    {:tablename form-id
                :data      data}}})

(defn webform-bulk-update-callback
  [_ [error _ {:keys [on-success on-failure]}]]
  (if error
    (do
      (when on-failure (on-failure))
      {:dispatch [:app.events/add-snack :error "Failed to update webform data"]})
    (do
      (when on-success (on-success))
      {:dispatch [:webforms.api/load-form-details]})))

(reg-event-http
  :webforms.edit/bulk
  webform-bulk-update-request
  webform-bulk-update-callback)
